// DEPENDENCIES ---------------------------------------------------------------- //

import React, { lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastContainer, Zoom } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import siteConfig from '@config';

import { useUser, useRooftop } from '@store';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';
import { checkForPermittedRole } from '@features/auth/services/authService';
import { persistButtonSettings, persistPlanMovesSettings, persistBrandingSettings } from '@utils/embedSettings';

import { css } from '@emotion/css';

import Header from '@features/layout/components/Header';
import Feature from '@features/layout/components/Feature';
import { ChatModalProvider } from '@components/Chat/ChatModalManager';
import EmbedPortalHost from '@features/products/EmbedPortalHost';
import { EmbedPlannerHost } from '@features/moveCreation/plannerHost';
import { isFullBleedPath } from '@features/products/isFullBleedPath';

// Lazy-load components that import from GraphQL schema to reduce initial bundle size
const RooftopLoader = lazy(() => import('@features/layout/components/RooftopLoader'));
const GlobalSmsSubscriptionProvider = lazy(() => import('@features/moves/components/GlobalSmsSubscriptionProvider'));

// The SMS subscription runs as a side effect, so it mounts beside the app tree rather than
// around it: the self-managed gate resolving never changes the element around the routes, which
// would remount the app. It loads only for a self-managed-capable customer.
const SmsSubscription = () => {
  const isSelfManaged = useValetSelfManaged();
  if (!isSelfManaged) return null;
  return (
    <Suspense fallback={null}>
      <GlobalSmsSubscriptionProvider>{null}</GlobalSmsSubscriptionProvider>
    </Suspense>
  );
};

// TYPES ---------------------------------------------------------------- //

interface LayoutProps {
  children: React.ReactNode;
}

// COMPONENT ---------------------------------------------------------------- //

const Layout = ({ children }: LayoutProps) => {
  const cls = useStyles();
  const location = useLocation();

  const user = useUser();
  const rooftop = useRooftop();
  const isPermitted = false; //checkForPermittedRole(user);

  // Persist embed settings from URL params to localStorage on every page load.
  // This ensures settings are available for internal navigation (e.g., clicking
  // "Plan Moves" button on dashboard will use settings from initial page load).
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    persistPlanMovesSettings(searchParams);
    persistBrandingSettings(searchParams);
    persistButtonSettings(searchParams);
  }, [location.search]);

  // The full-bleed surfaces (the maps) own their height instead of scrolling with the page, so
  // the feature region has to be a clipping flex column for the product shell's fill mode to have
  // a height to fill. Same list, same call the dealer makes into its app shell.
  const scroll = isFullBleedPath(location.pathname) ? 'none' : 'page';

  const layoutContent = (
    <>
      {user && isPermitted ? (
        <>
          <div className={cls.content}>
            <Header />
            <Feature scroll={scroll}>{children}</Feature>
          </div>
        </>
      ) : (
        <div className={cls.content}>
          <Feature scroll={scroll}>{children}</Feature>
        </div>
      )}
      <ToastContainer position='top-center' theme='dark' limit={3} transition={Zoom} />
    </>
  );

  return (
    // The portal-products host wraps everything: mounting it runs the per-customer product fetch
    // that the route table's product gates and the product shells read.
    <EmbedPortalHost>
      {/* The Move Planner's host, for the planner and for the planner pieces other surfaces render. */}
      <EmbedPlannerHost>
      <ChatModalProvider>
      {/* Lazy-load RooftopLoader after user authentication to defer GraphQL schema loading */}
      {user?.claims?.['x-hasura-customer-id'] && !rooftop && (
        <Suspense fallback={null}>
          <RooftopLoader customerId={user.claims['x-hasura-customer-id']} />
        </Suspense>
      )}

      <SmsSubscription />
      <div className={cls.layout}>{layoutContent}</div>
      </ChatModalProvider>
      </EmbedPlannerHost>
    </EmbedPortalHost>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = () => {
  const styles = {
    layout: css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100vw;
      height: 100vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    `,
    content: css`
      overflow: auto;
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
    `,
  };

  return styles;
};

// EXPORT ---------------------------------------------------------------- //

export default Layout;
